import api from '@/libs/axios';

const GUEST_CART_KEY = 'ec-cart-guest';
const GUEST_ADDRESS_KEY = 'ec-guest-address';
const CONFIG_CART_KEY = 'ec-config-cart';

const getCartStorageKey = (userId) => (userId ? `ec-cart-${userId}` : GUEST_CART_KEY);

const readCartFromStorage = (key) => {
  if (typeof localStorage === 'undefined') return [];
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to parse cart from storage', error);
    return [];
  }
};

const writeCartToStorage = (key, items) => {
  if (typeof localStorage === 'undefined') return;
  try {
    if (!items || items.length === 0) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(items));
  } catch (error) {
    console.error('Failed to persist cart to storage', error);
  }
};

const readGuestAddressFromStorage = () => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const stored = localStorage.getItem(GUEST_ADDRESS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to parse guest address from storage', error);
    return null;
  }
};

const writeGuestAddressToStorage = (address) => {
  if (typeof localStorage === 'undefined') return;
  try {
    if (!address) {
      localStorage.removeItem(GUEST_ADDRESS_KEY);
      return;
    }
    localStorage.setItem(GUEST_ADDRESS_KEY, JSON.stringify(address));
  } catch (error) {
    console.error('Failed to persist guest address to storage', error);
  }
};

const buildCartItemKey = (item) => {
  if (item?.ebook_id) return `ebook-${item.ebook_id}-${item.format}`;
  if (item?.collection_id) return `collection-${item.collection_id}-${item.format}`;
  if (item?.combo_id) return `combo-${item.combo_id}-${item.format}`;
  return `unknown-${item?.id || Math.random()}-${item?.format || 'none'}`;
};

const sanitizeCartItemForRequest = (item) => {
  const payload = {
    amount: item.amount || 1,
    format: item.format,
  };

  if (item.ebook_id) payload.ebook_id = item.ebook_id;
  if (item.collection_id) payload.collection_id = item.collection_id;
  if (item.combo_id) payload.combo_id = item.combo_id;

  return payload;
};

const initialGuestCart = readCartFromStorage(GUEST_CART_KEY);
const initialGuestAddress = readGuestAddressFromStorage();
const readConfigCartFromStorage = () => {
  if (typeof localStorage === 'undefined') return {};
  try {
    const stored = localStorage.getItem(CONFIG_CART_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Failed to parse config cart from storage', error);
    return {};
  }
};
const writeConfigCartToStorage = (config) => {
  if (typeof localStorage === 'undefined') return;
  try {
    if (!config || Object.keys(config).length === 0) {
      localStorage.removeItem(CONFIG_CART_KEY);
      return;
    }
    localStorage.setItem(CONFIG_CART_KEY, JSON.stringify(config));
  } catch (error) {
    console.error('Failed to persist config cart to storage', error);
  }
};
const initialConfigCart = readConfigCartFromStorage();
const hasUserInfo = (userInfo) => {
  if (!userInfo) return false;
  if (Array.isArray(userInfo)) return userInfo.length > 0;
  return Object.keys(userInfo).length > 0;
};

const buildIdempotencyKey = (prefix = 'mp') =>
  `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;

const extractPagbankToken = (settings = {}) => {
  if (!settings || typeof settings !== 'object') return null;
  const flowSettings = settings.payment_flow?.settings || {};
  const candidates = [
    settings?.transparent?.pagbank?.token,
    settings?.transparent?.pagseguro?.token,
    settings?.transparent?.pagback?.token,
    settings?.transparent?.token,
    settings?.pagbank?.token,
    settings?.pagseguro?.token,
    settings?.pagback?.token,
    settings?.pagbank?.sessionId,
    settings?.pagseguro?.sessionId,
    settings?.pagback?.sessionId,
    settings?.token_pagbank,
    settings?.token_pagseguro,
    settings?.token,
    settings?.session_id,
    flowSettings?.pagbank?.token,
    flowSettings?.pagseguro?.token,
    flowSettings?.pagback?.token,
    flowSettings?.token_pagbank,
    flowSettings?.token_pagseguro,
    flowSettings?.token,
    flowSettings?.session_id,
  ];
  return candidates.find(val => val && String(val).trim()) || null;
};

const customer = {

  state: {
    productsInCart: initialGuestCart,
    configCart: initialConfigCart,
    checkoutPaymentSettings: null,
    cartProductsAddress: initialGuestAddress,
    userInfo: null,
    guestAddress: initialGuestAddress,

    productsInCartLink: [],
    productsLinkAddress: {
      street: "",
      city: "",
      district: "",
      state: "",
      number: "",
      zip_code: "",
      country: "",
      complement: "",
    },
  },
  getters: {
    returnProductsInCart: state => state.productsInCart,
    returnCartProductsAddress: state => state.cartProductsAddress,
    returnConfigCart: state => state.configCart,
    returnCheckoutPaymentSettings: state => state.checkoutPaymentSettings,
    returnProductsInCartLink: state => state.productsInCartLink,
    returnProductsLinkAddress: state => state.productsLinkAddress,
    returnGuestAddress: state => state.guestAddress,
  },
  actions: {

    /* ///////////////////////
                              1.0
                      ////////////////////// */
    ecommerceFetchProduct: ({ commit }, idProduct) => new Promise(async (resolve, reject) => {
      await api.get(`/customer/stores/psiqueasy/ebooks/${idProduct}`).then(response => {
        resolve(response.data)
      }).catch(error => {
        reject(error.response.data.message)
      })
    }),
    ecommerceFetchCollection: ({ commit }, idProduct) => new Promise(async (resolve, reject) => {
      await api.get(`/customer/stores/psiqueasy/collections/${idProduct}`).then(response => {
        resolve(response.data)
      }).catch(error => {
        reject(error.response.data.message)
      })
    }),
    ecommerceFetchCombo: ({ commit }, idProduct) => new Promise(async (resolve, reject) => {
      await api.get(`/customer/stores/psiqueasy/combos/${idProduct}`).then(response => {
        resolve(response.data)
      }).catch(error => {
        reject(error.response.data.message)
      })
    }),
    buyProduct: ({ commit }, data) => new Promise(async (resolve, reject) => {
      await api.post(`/customer/stores/psiqueasy/purchases`, data).then(response => {
        resolve(response.data)
      }).catch(error => {
        reject(error.response.data)
      })
    }),
    makePayment: ({ commit }, data) => {
      return new Promise(async (resolve, reject) => {
        const params = new URLSearchParams({
          platform_payment: data.payment_gateway,
        });
        const headers = {};
        if (String(data.payment_gateway).toLowerCase().includes('mp')) {
          headers['X-Idempotency-Key'] = buildIdempotencyKey('mp-payment');
        }
        await api.get(`/customer/purchases/${data.idSale}/payment?${params.toString()}`, { headers }).then(response => {
          resolve(response.data)
        }).catch(error => {
          reject(error.response.data)
        })
      });
    },
    filterOptionsAPI: ({ commit }) => {
      return new Promise(async (resolve, reject) => {
        await api.get(`/customer/stores/psiqueasy/categories`).then(response => {
          resolve(response.data)
        }).catch(error => {
          reject(error.response.data.message)
        })
      });
    },
    async fetchCheckoutPaymentSettings({ commit, state }, payload = {}) {
      try {
        const storeNick =
          payload.storeNick
          || state.configCart?.store?.nickname
          || state.configCart?.store_nick
          || state.configCart?.storeNick
          || 'psiqueasy';
        const response = await api.get(`/customer/store/${storeNick}/checkout/store/config`);
        const raw = response?.data ?? response ?? {};
        const settings = raw?.data || raw?.payload || raw?.settings || raw?.config || raw;
        const storeCheckoutId =
          settings?.stores_checkout_id
          || raw?.stores_checkout_id
          || raw?.data?.stores_checkout_id
          || null;
        if (settings) {
          commit('setCheckoutPaymentSettings', settings);
          const token = extractPagbankToken(settings);
          commit('setConfigCart', {
            mode: settings.mode,
            checkout_payment_settings: settings,
            payment_settings: settings,
            pagbank_token: token,
            pagback_token: token,
            pagbank_session_id: token,
            pagback_session_id: token,
            stores_checkout_id: storeCheckoutId,
            payment_flow: {
              type: settings.mode,
              settings,
            },
          });
        }
        return settings;
      } catch (error) {
        console.log('[checkout] Falha ao carregar configuração de pagamento', error);
        throw error?.response || error;
      }
    },

    // ------------------------------------------------
    // 2.0
    // Product Actions
    // ------------------------------------------------

    getProductsInCart: ({ commit }) => {
      return new Promise((resolve, reject) => {
        api.get('/customer/ecommerce/cart').then(response => {
          commit('addProductsInCart', response.data);

          resolve(response.data)
        }).catch(error => {
          reject(error.response.data.message)
        })
      });
    },
    addProductInCart({ commit }, params) {
      return new Promise((resolve, reject) => {
        api
          .post('/customer/ecommerce/cart', params).then(response => {
            commit('changeIdItemCart', response.data);
            resolve(response.data);
          }).catch(error => {
            reject(error.response.data.message);
          });
      });
    },
    destroyProductFromCart({ commit }, itemId) {
      return new Promise((resolve, reject) => {
        api
          .delete(`/customer/ecommerce/cart/${itemId}`).then(response => {
            //commit('destroyItemInCart', itemId);
            resolve(response.data);
          }).catch(error => {
            reject(error.response.data.message);
          });
      });
    },
    changeAmountItemCart({ commit }, params) {
      return new Promise((resolve, reject) => {
        const body = {
          amount: params.amount,
        };

        api
          .patch(`/customer/ecommerce/cart/${params.itemId}`, body).then(response => {
            //commit('changeAmountItemInCart', params);
            resolve(response.data);
          }).catch(error => {
            reject(error.response.data.message);
          });
      })
    },

    getDetailLinkPayData: ({ commit }, link) => {
      return new Promise(async (resolve, reject) => {
        await api.get(`/stores/psiqueasy/payment/link/${link}`).then(response => {
          commit('addProductsInCartLink', response.data);
          resolve(response.data)
        }).catch(error => {
          reject(error.response.data)
        })
      })
    },
    getDetailCepAddress: ({ commit }, zip_Code) => {
      return new Promise(async (resolve, reject) => {

        await fetch(`https://viacep.com.br/ws/${zip_Code}/json/`).then(response => {
          resolve(response.json())
        }).catch(error => {
          reject(error.response)
        })
      })
    },

    buyProductLinkExt: ({ commit }, data) => new Promise(async (resolve, reject) => {
      await api.post(`/stores/psiqueasy/payment/link/${data.linkId}`, data).then(response => {
        resolve(response.data)
      }).catch(error => {
        reject(error.response.data)
      })
    }),
    async clearFullCart({ state, commit }) {
      const userId = state.userInfo?.id;
      const doClearLocally = () => {
        commit('clearItemsCart');
        commit('clearGuestAddress');
        commit('setConfigCart', {});
      };

      if (!userId) {
        doClearLocally();
        return;
      }

      try {
        await api.delete('/customer/ecommerce/cart');
      } catch (error) {
        // mesmo em erro, limpamos local para não deixar resíduos
      } finally {
        doClearLocally();
      }
    },
    async syncGuestCartToUser({ state, dispatch }) {
      const userId = state.userInfo?.id;

      await dispatch('getProductsInCart').catch(() => { });

      const guestCart = readCartFromStorage(GUEST_CART_KEY);
      if (!userId || guestCart.length === 0) {
        return;
      }

      const aggregatedGuestCart = guestCart.reduce((acc, current) => {
        const key = buildCartItemKey(current);
        if (!acc[key]) {
          acc[key] = { ...current };
        } else {
          const currentAmount = Number(acc[key].amount) || 0;
          const incomingAmount = Number(current.amount) || 0;
          acc[key].amount = currentAmount + incomingAmount;
        }
        return acc;
      }, {});

      const existingItems = [...state.productsInCart];
      const existingMap = new Map(
        existingItems.map((item) => [buildCartItemKey(item), item])
      );

      let hadErrors = false;
      for (const item of Object.values(aggregatedGuestCart)) {
        const key = buildCartItemKey(item);
        const match = existingMap.get(key);
        try {
          if (match) {
            const newAmount =
              (Number(match.amount) || 0) + (Number(item.amount) || 0);
            if (newAmount !== match.amount) {
              await dispatch('changeAmountItemCart', {
                itemId: match.id,
                amount: newAmount,
              });
              match.amount = newAmount;
              existingMap.set(key, match);
            }
          } else {
            await dispatch('addProductInCart', sanitizeCartItemForRequest(item));
          }
        } catch (error) {
          hadErrors = true;
          console.error('Failed to sync cart item', item, error);
        }
      }

      if (!hadErrors) {
        writeCartToStorage(GUEST_CART_KEY, []);
      }

      await dispatch('getProductsInCart').catch(() => { });
    },
    persistCartForGuest({ state }) {
      writeCartToStorage(GUEST_CART_KEY, state.productsInCart || []);
    },
  },
  mutations: {
    addProductsInCart: (state, stockList = {}) => {
      const userId = state.userInfo?.id;
      const storageKey = getCartStorageKey(userId);
      const serverItems = stockList.cart_items || [];
      const items = serverItems.length ? serverItems : readCartFromStorage(storageKey);
      state.productsInCart = items;
      state.configCart = stockList.cart_config || state.configCart || {};
      if (state.configCart) writeConfigCartToStorage(state.configCart);
      writeCartToStorage(storageKey, state.productsInCart);
    },
    setConfigCart: (state, config = {}) => {
      const current = state.configCart || {};
      state.configCart = { ...current, ...(config || {}) };
      writeConfigCartToStorage(state.configCart);
    },
    setCheckoutPaymentSettings: (state, settings = null) => {
      state.checkoutPaymentSettings = settings;
    },
    changeAmountItemInCart: (state, data) => {
      const indexItem = state.productsInCart.findIndex((res) => res.id == data.itemId);
      if (indexItem > -1) {
        state.productsInCart[indexItem].amount = data.amount;
      }
      writeCartToStorage(getCartStorageKey(state.userInfo?.id), state.productsInCart);

    },
    destroyItemInCart: (state, itemId) => {
      const indexItem = state.productsInCart.findIndex((res) => res.id == itemId);
      if (indexItem > -1) {
        state.productsInCart.splice(indexItem, 1);
      }
      writeCartToStorage(getCartStorageKey(state.userInfo?.id), state.productsInCart);
    },
    addItemInCart: (state, data) => {
      const indexItem = state.productsInCart.findIndex((res) => res.id == data.id);
      if (indexItem > -1) {
        state.productsInCart.splice(indexItem, 1);
      }
      state.productsInCart.push(data);

      writeCartToStorage(getCartStorageKey(state.userInfo?.id), state.productsInCart);
    },
    addAddressCart: (state, data) => {
      state.cartProductsAddress = data;
      if (!hasUserInfo(state.userInfo)) {
        state.guestAddress = data;
        writeGuestAddressToStorage(data);
      } else {
        state.guestAddress = null;
        writeGuestAddressToStorage(null);
      }
    },
    clearGuestAddress: (state) => {
      state.guestAddress = null;
      if (!hasUserInfo(state.userInfo)) {
        state.cartProductsAddress = null;
      }
      writeGuestAddressToStorage(null);
    },
    clearItemsCart: (state) => {
      state.productsInCart = [];
      writeCartToStorage(getCartStorageKey(state.userInfo?.id), state.productsInCart);
    },
    removeCartItemsByPayment(state, paymentProducts = []) {
      if (!Array.isArray(paymentProducts) || paymentProducts.length === 0) {
        return;
      }
      const normalized = paymentProducts.map(item => {
        const format = item.format || item.format_product || null;
        return {
          ebook_id: item.ebook_id || item.ebookId || (item.ebook ? item.ebook.id : null),
          collection_id: item.collection_id || item.collectionId || (item.collection ? item.collection.id : null),
          combo_id: item.combo_id || item.comboId || (item.combo ? item.combo.id : null),
          format,
        };
      });

      const shouldRemove = (cartItem) => {
        return normalized.some(product => {
          const formatMatches = !product.format || product.format === cartItem.format;
          if (!formatMatches) return false;
          if (product.ebook_id && cartItem.ebook_id) {
            return Number(product.ebook_id) === Number(cartItem.ebook_id);
          }
          if (product.collection_id && cartItem.collection_id) {
            return Number(product.collection_id) === Number(cartItem.collection_id);
          }
          if (product.combo_id && cartItem.combo_id) {
            return Number(product.combo_id) === Number(cartItem.combo_id);
          }
          return false;
        });
      };

      state.productsInCart = state.productsInCart.filter(cartItem => !shouldRemove(cartItem));
      writeCartToStorage(getCartStorageKey(state.userInfo?.id), state.productsInCart);
    },
    changeIdItemCart: (state, data) => {
      let indexItem = -1;

      if (data.ebook_id) {
        indexItem = state.productsInCart.findIndex((res) => res.ebook_id == data.ebook_id && res.format == data.format);
      } else if (data.collection_id) {
        indexItem = state.productsInCart.findIndex((res) => res.collection_id == data.collection_id && res.format == data.format);
      } else if (data.combo_id) {
        indexItem = state.productsInCart.findIndex((res) => res.combo_id == data.combo_id && res.format == data.format);
      }
      if (indexItem > -1) {
        state.productsInCart[indexItem].id = data.id;
        writeCartToStorage(getCartStorageKey(state.userInfo?.id), state.productsInCart);
      }
    },
    localeUserInfoCart(state, userInfo) {
      state.userInfo = userInfo;
      if (hasUserInfo(userInfo)) {
        state.guestAddress = null;
        writeGuestAddressToStorage(null);
      }
    },
    setFirstStor(state, userInfo) {
      state.userInfo = userInfo;
      if (hasUserInfo(userInfo)) {
        state.guestAddress = null;
        writeGuestAddressToStorage(null);
      }
    },

    addProductsInCartLink: (state, produtctsLink) => {
      state.productsInCartLink = produtctsLink;
    },
  },
}

export default customer;
